import { ImageLoaderProps } from "next/image";

/**
 * Custom Sanity Image Loader for Next.js Image component
 *
 * This loader receives URLs that are already built by @sanity/image-url
 * and enhances them with responsive sizing and optimization parameters.
 *
 * Features:
 * - Responsive image sizing (updates width parameter)
 * - Quality optimization
 * - DPR (Device Pixel Ratio) support for Retina displays
 */
export default function sanityLoader({
  src,
  width,
  quality
}: ImageLoaderProps): string {
  const url = new URL(src);

  // Cap at 1920 × 2 DPR = 3840px
  const clampedWidth = Math.min(width, 3840);

  // Scale height proportionally to maintain the original aspect ratio.
  // This prevents removing h entirely (which would serve full-height images)
  // while keeping the ratio from the base URL built by buildSrc.
  const originalW = Number(url.searchParams.get("w")) || 0;
  const originalH = Number(url.searchParams.get("h")) || 0;

  url.searchParams.set("w", clampedWidth.toString());

  if (originalW && originalH) {
    const scaledH = Math.round(clampedWidth * (originalH / originalW));
    url.searchParams.set("h", scaledH.toString());
  }

  if (quality) {
    url.searchParams.set("q", quality.toString());
  }

  return url.href;
}
